<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import CsatMetrics from './components/CsatMetrics.vue';
import CsatTable from './components/CsatTable.vue';
import CsatFilters from './components/Csat/CsatFilters.vue';
import CsatQualityBreakdown from './components/CsatQualityBreakdown.vue';
import { generateStatisticsFileName } from '../../../../helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';

export default {
  name: 'CsatResponses',
  components: {
    CsatMetrics,
    CsatTable,
    CsatFilters,
    CsatQualityBreakdown,
    ReportHeader,
    V4Button,
  },
  data() {
    return {
      pageIndex: 0,
      pageSize: 25,
      from: 0,
      to: 0,
      comparisonPeriods: [],
      userIds: [],
      inboxIds: [],
      teamIds: [],
      ratings: [],
      labelTitles: [],
      excludeLabelTitles: [],
      resolutionMethod: 'all',
      hasComment: 'all',
      breakdownGroupBy: 'agent',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledOnAccount: 'accounts/isFeatureEnabledonAccount',
      getAccount: 'accounts/getAccount',
      breakdown: 'csat/getBreakdown',
      uiFlags: 'csat/getUIFlags',
    }),
    displayType() {
      const account = this.getAccount(this.accountId);
      return account?.csat_config?.display_type ?? 'emoji';
    },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        comparisonPeriods: this.comparisonPeriods,
        user_ids: this.userIds,
        inbox_id: this.inboxIds,
        team_id: this.teamIds,
        rating: this.ratings,
        label_title: this.labelTitles,
        exclude_label_title: this.excludeLabelTitles,
        resolution_method: this.resolutionMethod,
        has_comment: this.hasComment,
      };
    },
    archiveEntityFilters() {
      return {
        agentIds: this.userIds,
        inboxIds: this.inboxIds,
        teamIds: this.teamIds,
        labelTitles: this.labelTitles,
        resolutionMethod: this.resolutionMethod,
      };
    },
    isTeamsEnabled() {
      return this.isFeatureEnabledOnAccount(
        this.accountId,
        FEATURE_FLAGS.TEAM_MANAGEMENT
      );
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('labels/get');
  },
  methods: {
    getAllData() {
      try {
        this.$store.dispatch('csat/getMetrics', this.requestPayload);
        this.$store.dispatch('csat/getDynamics', this.requestPayload);
        this.fetchBreakdown();
        this.getResponses();
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
    getResponses() {
      this.$store.dispatch('csat/get', {
        page: this.pageIndex + 1,
        per_page: this.pageSize,
        ...this.requestPayload,
      });
    },
    fetchBreakdown() {
      this.$store.dispatch('csat/getBreakdown', {
        ...this.requestPayload,
        group_by: this.breakdownGroupBy,
      });
    },
    downloadReports() {
      this.$store.dispatch('csat/downloadCSATReports', {
        fileName: generateStatisticsFileName({
          section: 'csat',
          from: this.from,
          to: this.to,
        }),
        ...this.requestPayload,
      });
    },
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.getResponses();
    },
    onPageSizeChange(pageSize) {
      this.pageSize = pageSize;
      this.pageIndex = 0;
      this.getResponses();
    },
    onBreakdownGroupChange(groupBy) {
      this.breakdownGroupBy = groupBy;
      this.fetchBreakdown();
    },
    onFilterChange({
      from,
      to,
      comparisonPeriods,
      selectedAgents,
      selectedInboxes,
      selectedTeams,
      selectedRatings,
      selectedLabels,
      excludedLabels,
      resolutionMethod,
      hasComment,
    }) {
      if (this.from !== 0 && this.to !== 0) {
        useTrack(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: 'csat',
        });
      }

      this.from = from;
      this.to = to;
      this.comparisonPeriods = comparisonPeriods || [];
      this.userIds = selectedAgents.map(el => el.id);
      this.inboxIds = selectedInboxes.map(el => el.id);
      this.teamIds = selectedTeams.map(el => el.id);
      this.ratings = selectedRatings.map(el => el.value);
      this.labelTitles = selectedLabels.map(el => el.value);
      this.excludeLabelTitles = excludedLabels.map(el => el.value);
      this.resolutionMethod = resolutionMethod || 'all';
      this.hasComment = hasComment || 'all';
      this.pageIndex = 0;

      this.getAllData();
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('CSAT_REPORTS.HEADER')">
    <V4Button
      :label="$t('CSAT_REPORTS.DOWNLOAD')"
      icon="i-ph-file-arrow-down"
      size="sm"
      @click="downloadReports"
    />
  </ReportHeader>

  <div class="flex flex-col gap-6">
    <p class="text-sm text-n-slate-11">
      {{ $t('CSAT_REPORTS.DESCRIPTION') }}
    </p>

    <CsatFilters
      :show-team-filter="isTeamsEnabled"
      :display-type="displayType"
      @filter-change="onFilterChange"
    />
    <CsatMetrics
      :display-type="displayType"
      :from="from"
      :to="to"
      :entity-filters="archiveEntityFilters"
    />
    <CsatQualityBreakdown
      :rows="breakdown"
      :group-by="breakdownGroupBy"
      :is-loading="uiFlags.isFetchingBreakdown"
      @group-change="onBreakdownGroupChange"
    />
    <CsatTable
      :page-index="pageIndex"
      :page-size="pageSize"
      @page-change="onPageNumberChange"
      @page-size-change="onPageSizeChange"
    />
  </div>
</template>
